import React, { useContext, useEffect, useState } from "react";
import ChatbotDialog from "@/components/dialog/ChatbotDialog.js";
import ChatbotOpenBubble, {
  ChatbotBubbleOptions,
} from "@/components/dialog/ChatbotOpenBubble.js";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/shadcn/card.tsx";
import { Sheet, SheetContent } from "@/components/shadcn/sheet";
import { Button } from "@/components/shadcn/button";

import { contrastColor, hexToRGB } from "@/utils/colors";

import "rc-slider/assets/index.css";
import { resetSession } from "@/utils/utils";

import "@/_embed/styles.scss";

import { useTranslation } from "react-i18next";
import { ChatbotContext } from "@/store/context/ChatbotContext.tsx";
import Loading from "@/components/Loading";
import { ChatBotDialogOptions, WidgetOptions } from "@/models/chatbot.ts";
import {Icon} from "@/components/Icon.tsx";
import ModelSettings from "@/pages/chatbot/settings/ModelSettings.tsx";
import { saveSettings } from "@/store/chatbot.ts";
import { useParams } from "react-router-dom";
import {useSidebar} from "@/components/shadcn/sidebar.tsx";

const Playground = () => {
  const { t } = useTranslation();
  const { isMobile } = useSidebar();

  const { chatbotID } = useParams();
  const { chatSettings, setSettings, viewChatbot, invalidateChatbot } =
    useContext(ChatbotContext);

  const [widgetOptions, setWidgetOptions] = useState<WidgetOptions | null>();
  const [dialogOptions, setDialogOptions] =
    useState<ChatBotDialogOptions | null>();
  const [bubbleOptions, setBubbleOptions] =
    useState<ChatbotBubbleOptions | null>();

  const [isDialogOpened, setDialogOpened] = React.useState(true);
  const [isSheetOpened, setSheetOpened] = React.useState(false);

  useEffect(() => {
    setWidgetOptions(viewChatbot?.widgetSettings);
    setDialogOptions(viewChatbot?.widgetSettings.dialogSettings);
    setBubbleOptions(viewChatbot?.widgetSettings.bubbleSettings);
  }, [viewChatbot?.id]);

  if (
    !viewChatbot ||
    !widgetOptions ||
    !bubbleOptions ||
    !dialogOptions ||
    !chatSettings
  ) {
    return <Loading />;
  }

  // helpers
  let bubbleBgRGB = hexToRGB(bubbleOptions.iconBgColor);

  return (
    <>
      <div className="max-w-[1500px]">
        <Card>
          <CardHeader className="hidden md:flex flex-row items-center justify-between py-1 pr-2 pl-4">
            <CardTitle>{t("sidebar.playground")}</CardTitle>
            <Button
              onClick={() => {
                setSheetOpened(true);
              }}
              className="[&_svg]:size-5"
            >
              <Icon icon="fluent:settings-32-regular"></Icon>
              {t("sidebar.ai_settings")}
            </Button>
          </CardHeader>
          <CardContent className="p-0 dots">
            <div className="flex justify-center items-center min-h-[80vh]">
              <div
                id="wizychat-container"
                className="flex justify-end flex-col !relative min-h-[70vh] min-w-[350px] w-full lg:w-3/4 2xl:w-1/2"
                style={
                  bubbleOptions.position === "right"
                    ? { alignItems: "flex-end" }
                    : { alignItems: "flex-start", left: "1.5rem" }
                }
              >
                {isDialogOpened && dialogOptions && (
                  <div
                    className={
                      "mb-6 md:absolute h-[85vh] md:h-[60vh] md:bottom-20 shadow-2xl w-full " +
                      (bubbleOptions.position === "right"
                        ? "right-0"
                        : "left-0")
                    }
                  >
                    <ChatbotDialog
                      messages={null}
                      token={viewChatbot.token}
                      hideCloseBtn={isMobile}
                      design={dialogOptions}
                      onClose={() => {
                        setDialogOpened(false);
                      }}
                      onRefresh={() => {
                        resetSession(viewChatbot.token);
                        setDialogOpened(false);
                        setTimeout(() => {
                          setDialogOpened(true);
                        }, 200);
                      }}
                      showExpandBtn={false}
                      onExpand={() => {}}
                    />
                  </div>
                )}

                {/*Initial question outside*/}
                {widgetOptions.showQuestionsOutside &&
                  !isDialogOpened &&
                  dialogOptions.initialMessage && (
                    <div className="wz-question-outside-wrapper">
                      <div
                        className="wz-question-outside"
                        style={{
                          backgroundColor: bubbleOptions.iconBgColor,
                          color: contrastColor(
                            bubbleBgRGB.r,
                            bubbleBgRGB.g,
                            bubbleBgRGB.b,
                          ),
                        }}
                        onClick={() => {
                          setDialogOpened((state) => {
                            return !state;
                          });
                        }}
                      >
                        {dialogOptions.initialMessage}
                      </div>
                    </div>
                  )}

                <div className="hidden md:block">
                  <ChatbotOpenBubble
                    {...bubbleOptions}
                    opened={isDialogOpened}
                    onClick={() => {
                      setDialogOpened((state) => {
                        return !state;
                      });
                    }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Sheet open={isSheetOpened} onOpenChange={setSheetOpened}>
        <SheetContent className="md:min-w-[600px] xl:min-w-[900px]">
          <ModelSettings
            viewSettings={chatSettings}
            viewChatbot={viewChatbot}
            setSettings={setSettings}
          />

          <div className="flex justify-end mt-6">
            {/*@ts-ignore*/}

            <Button
              onClick={async () => {
                if (chatbotID) {
                  await saveSettings(
                    chatbotID,
                    chatSettings,
                    invalidateChatbot,
                  );
                }
              }}
            >
              {t("save_changes")}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default Playground;
